/**
 * Décision du Conseil constitutionnel
 * Route : /conseil-constitutionnel/:numero   (ex. 2025-1180-qpc)
 */
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Loader } from '@/components/Loader';
import { PublicFooter, PublicHeader } from '@/components/jurisprudence';

import { type ConstitSummary, NatureBadge, formatDate } from './index';

interface ConstitDecision extends ConstitSummary {
  texte: string;
  saisines: string;
  observations: string;
  titre_jo: string;
  url_cc: string;
  url_legifrance: string;
}

function Section({ title, text }: { title: string; text?: string }) {
  if (!text) return null;
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
        {title}
      </h2>
      <div className="text-[15px] leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-line">
        {text}
      </div>
    </section>
  );
}

export default function ConstitDecisionPage() {
  const { t } = useTranslation();
  const { numero = '' } = useParams();
  const [decision, setDecision] = useState<ConstitDecision | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setDecision(null);
    setNotFound(false);
    fetch(`/api/constit/decision/${encodeURIComponent(numero)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setDecision)
      .catch(() => setNotFound(true));
  }, [numero]);

  return (
    <>
      {decision && (
        <Helmet>
          <title>{`${decision.numero} — ${decision.titre} | SmartLawyer`}</title>
          <meta
            name="description"
            content={`${t('constit.hero.title')}, ${formatDate(
              decision.date
            )} : ${decision.solution || decision.titre}`}
          />
        </Helmet>
      )}
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
        <PublicHeader />
        <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">
          <Link
            to="/conseil-constitutionnel"
            className="text-sm text-violet-600 dark:text-violet-400 hover:underline"
          >
            ← {t('constit.decision.back')}
          </Link>
          {notFound ? (
            <p className="mt-8 text-gray-500">
              {t('constit.decision.not_found')}
            </p>
          ) : !decision ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : (
            <article className="mt-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-semibold text-violet-600 dark:text-violet-400">
                  {decision.numero}
                </span>
                <NatureBadge nature={decision.nature} />
              </div>
              <h1 className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100 leading-snug">
                {decision.titre}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {t('constit.decision.date')} {formatDate(decision.date)}
                {decision.ecli && (
                  <span className="ml-2 font-mono text-xs">
                    {decision.ecli}
                  </span>
                )}
              </p>
              {decision.solution && (
                <p className="mt-4 inline-block rounded-lg bg-violet-50 dark:bg-violet-900/20 px-3 py-2 text-sm text-violet-800 dark:text-violet-300">
                  <strong>{t('constit.decision.solution')} :</strong>{' '}
                  {decision.solution}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-3 text-sm">
                {decision.url_cc && (
                  <a
                    href={decision.url_cc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 dark:text-violet-400 hover:underline"
                  >
                    {t('constit.decision.official')} ↗
                  </a>
                )}
                {decision.url_legifrance && (
                  <a
                    href={decision.url_legifrance}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 dark:text-violet-400 hover:underline"
                  >
                    {t('constit.decision.legifrance')} ↗
                  </a>
                )}
              </div>
              <Section
                title={t('constit.decision.text')}
                text={decision.texte}
              />
              <Section
                title={t('constit.decision.saisines')}
                text={decision.saisines}
              />
              <Section
                title={t('constit.decision.observations')}
                text={decision.observations}
              />
            </article>
          )}
        </main>
        <PublicFooter />
      </div>
    </>
  );
}
